/**
 * 陨坑地形剖面 —— 纯数学，无副作用、不碰 Three.js 场景。
 *
 * 这是把 `main.ts` 拆成模块的第一刀，挑它是因为它**依赖最少**：只用到两个
 * 数学小工具，却被 16 处调用（建筑贴地、道路弧带、电缆管道、镜头拾取、
 * 覆盖层……）。抽出来之后这些消费者改为 import，声明顺序不再由文件行号决定。
 *
 * 为什么要拆：`main.ts` 已 2600 行，模块级 `const` 的**声明顺序**与函数的
 * **执行顺序**耦死，同一类 TDZ 缺陷在 G008A / G009 / G014 里连续崩了四次
 * （见 docs/goals/reports/G014-report.md 第 9 节）。ES 模块的 import 图
 * 会按依赖顺序初始化，能从结构上消除这类问题。
 */

/* ===================== 数学工具 ===================== */

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export const clamp = (v: number, a: number, b: number): number =>
  Math.max(a, Math.min(b, v));

/** 平滑阶跃（smoothstep）。 */
export const sstep = (a: number, b: number, v: number): number => {
  const t = clamp((v - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
};

/**
 * 渲染专用的种子随机源。
 *
 * **绝不可用于模拟结果** —— 那必须走内核的 `SeededRng`（`AGENTS.md` 明令
 * 禁止 `Math.random` 决定模拟）。此处只用于建筑摆放的视觉抖动、程序化贴图
 * 等纯外观扰动；换个种子只会让画面略有不同，不影响任何权威状态。
 */
export function createRenderRng(seed = 20240): () => number {
  let s = seed;
  return () => {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* ===================== 陨坑剖面常量 ===================== */

/** 坑底半径。与 BUILD_UPSCALE=1.6 同比例放大，保持建筑密度不变。 */
export const FLOOR_R = 384;
/** 坑缘半径。 */
export const RIM_R = 688;
/** 坑壁总高。 */
export const WALL_H = 264;
/** 坑外平原基准高度（低于环山，坑内仍看得见坑壁）。 */
export const OUTER_H = 104;
/** 远景山脊环：起止半径与最大起伏。 */
export const RIDGE_R0 = 2150;
export const RIDGE_R1 = 3950;
export const RIDGE_H = 430;
/** 地形外缘。**必须小于相机 far**，否则远山会被裁掉。 */
export const TERR_R = 4400;

/* ===================== 高度场 ===================== */

/**
 * 任意世界坐标处的地表高度。
 *
 * **逐字迁移自 main.ts，未改动任何公式** —— 只加类型标注，并把
 * `THREE.MathUtils.clamp` 换成本模块的 `clamp`（语义完全相同，
 * 都是 `Math.max(a, Math.min(b, v))`），以免纯数学模块依赖 Three.js。
 *
 * 迁移安全网见 test/terrain-profile.test.ts：对 60 个采样点断言黄金值。
 */
export function craterHeight(x: number, z: number): number {
  const r=Math.hypot(x,z);
  // large-scale undulation + detail noise (kept subtle so floor stays readable)
  const noise=(Math.sin(x*.03)*Math.cos(z*.028)*2.2)+(Math.sin(x*.13)*Math.sin(z*.11)*0.9);
  let h: number;
  if(r<=FLOOR_R){
    h=noise*0.9;                                   // flat-ish floor
  } else if(r>=RIM_R){
    // ---- 坑外：环山外坡 -> 缓降平原 -> 远景山脊环 ----
    // 旧版到这里就停了，而地形网格只有 ±575m（比 RIM_R=688 还小），
    // 坑壁根本没画完，外面直接是虚空 —— 这是「四周没有景观」的真正原因。
    const out=r-RIM_R;
    const apron=WALL_H + Math.min(out/160,1)*38;    // 环山外坡顶
    const fall=Math.max(0,1-Math.max(0,out-160)/760);
    h=OUTER_H + (apron-OUTER_H)*fall*fall;          // 从坡顶平滑落到外缘平原
    // 平原起伏（低频，避免读作一块平板）
    h+=Math.sin(x*.0042)*Math.cos(z*.0037)*13 + Math.sin(x*.0111+1.3)*Math.sin(z*.0093)*6;
    // 远景山脊环：按方位角叠三个频率，形成不重复的天际线
    if(r>RIDGE_R0){
      const a=Math.atan2(z,x);
      const ridge=(Math.sin(a*3.1+1.7)*.5+.5)*.58
                 +(Math.sin(a*6.7-0.9)*.5+.5)*.27
                 +(Math.sin(a*13.3+2.4)*.5+.5)*.15;
      const u=Math.min((r-RIDGE_R0)/(RIDGE_R1-RIDGE_R0),1);
      const env=Math.sin(Math.min(u,1)*Math.PI)**0.75;   // 中段最高，两端归零
      h+=ridge*RIDGE_H*env;
    }
    h+=noise;
  } else {
    // 完美同心的阶地读起来像唱片纹。按方位角叠三个频率扰动剖面进度，
    // 让每一级阶地的边缘沿圆周起伏、坍塌处深浅不一。
    const a=Math.atan2(z,x);
    const wob=Math.sin(a*3.7+0.9)*.055+Math.sin(a*7.1-1.8)*.032+Math.sin(a*11.3+2.6)*.018;
    const t=clamp((r-FLOOR_R)/(RIM_R-FLOOR_R)+wob,0,1);
    let prof=Math.pow(t,1.55);
    // carve terraces
    const steps=4, f=prof*steps, fi=Math.min(Math.floor(f),steps-1), fr=f-fi;
    prof=(fi+Math.pow(fr,1.35))/steps;
    h=prof*WALL_H + noise*(0.5+t);
    // 坑壁冲沟：沿坡向下切的浅沟，塌积物必然形成的地貌
    h-=Math.abs(Math.sin(a*22.0))*t*(1-t)*17;
  }
  return h;
}

/** 0 = 坑底，1 = 坑缘。用于按位置插值坑壁相关的表现参数。 */
export function wallFactor(x: number, z: number): number {
  return sstep(FLOOR_R, RIM_R, Math.hypot(x, z));
}
